import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { deleteTag } from '@/api/tags'
import type { TagResponseDto } from '@/api/generated/api'
import { tagsKeys, taggingsKeys, movesKeys, seriesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to delete a tag
 *
 * Handles:
 * - API call to delete tag (only if not in use)
 * - Cache invalidation for all tag queries
 * - Navigation back to tag management
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const deleteTag = useDeleteTag()
 *
 * const handleDelete = async (tagId: string) => {
 *   await deleteTag.mutateAsync(tagId)
 * }
 * ```
 */
export function useDeleteTag() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (tagId: string) => deleteTag(tagId),
    onSuccess: (_data, tagId) => {
      // Read the deleted tag's name from the detail cache (populated upstream
      // by useTag on TagDetailPage) so the toast can name what was deleted.
      // Fall back to the generic copy on cache miss.
      const cached = queryClient.getQueryData<TagResponseDto>(
        tagsKeys.detail(tagId),
      )

      // Invalidate all affected queries (backend cascade deletes taggings for this tag)
      queryClient.invalidateQueries({ queryKey: tagsKeys.all })
      queryClient.invalidateQueries({ queryKey: taggingsKeys.all })
      queryClient.invalidateQueries({ queryKey: movesKeys.all })
      queryClient.invalidateQueries({ queryKey: seriesKeys.all })

      // Show success toast
      toast({
        title: 'Tag deleted',
        description: cached?.name
          ? `"${cached.name}" has been deleted.`
          : 'The tag has been deleted successfully.',
      })

      // Navigate back to tag management
      navigate('/tags')
    },
    onError: (error: Error) => {
      // The API returns a structured 409 body when the tag is still attached
      // to moves/series: `{ code, attachedCount, message }`. Narrow through
      // Record<string, unknown> views so no `as` cast is needed (project
      // rule: no `as`) and no AxiosError import.
      const isRecord = (v: unknown): v is Record<string, unknown> =>
        typeof v === 'object' && v !== null
      let description = error.message || 'An unexpected error occurred'
      if (
        isRecord(error) &&
        isRecord(error.response) &&
        isRecord(error.response.data)
      ) {
        const { attachedCount } = error.response.data
        if (typeof attachedCount === 'number') {
          description = `This tag is attached to ${attachedCount} item(s). Remove it from those items before deleting.`
        }
      }

      toast({
        title: 'Cannot delete tag',
        description,
        variant: 'destructive',
      })
    },
  })
}
